import { Redirect, Route, Switch } from 'react-router'

import { ProtectedRoute } from '@webapp/app'
import { useAppRoutes, useFeatureFlag } from '@webapp/hooks'
import { useModuleConfig } from '@webapp/modules/expense/module/useModuleConfig'
import { Permissions } from '@webapp/platform/iam'
import { TravelApprovalsPage } from './TravelApprovalsPage'
import { TravelDetailsPage } from './TravelDetailsPage'
import { TravelEditPage } from './TravelEditPage'
import { TravelRequestPage } from './TravelRequestPage'
import { TravelStatusPage } from './TravelStatusPage'
import {
  TravelApprovalsPageTabStatuses,
  TravelStatusPageTabStatuses,
} from './types'

// The uuid constraint keeps the `request`/`requests`/`approvals` segments from
// ever matching the detail route, making route order independent of the
// status-param routes below.
const TRAVEL_UUID_PARAM = ':uuid([0-9a-f]{32})'
const TRAVEL_STATUS_PARAM = ':status(all|pending|approved|denied)'

export const TravelsRouter = () => {
  const { brp, url } = useAppRoutes()

  const PSERV_346_BASE_MIGRATION = useFeatureFlag('PSERV_346_BASE_MIGRATION')

  const { showExpenseRoutes } = useModuleConfig()

  if (!showExpenseRoutes) return <Redirect to={url('DashboardPage')} />

  return (
    <Switch>
      {/* PSERV_346: flag-off falls back to the Angular travel hash routes. */}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('TravelEditPage', { uuid: TRAVEL_UUID_PARAM })}
          to='/#/travels/:uuid'
        />
      )}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('TravelDetailsPage', { uuid: TRAVEL_UUID_PARAM })}
          to='/#/travels/:uuid'
        />
      )}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect from={brp('TravelRequestPage')} to='/#/travels/request' />
      )}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('TravelApprovalsPage', { status: ':status?' })}
          to='/#/travels/approvals'
        />
      )}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect from='/travels' to='/#/travels/requests' />
      )}

      {/* /travels lands on the pending requests list */}
      <Redirect
        exact
        from='/travels'
        to={url('TravelStatusPage', {
          status: TravelStatusPageTabStatuses.PENDING,
        })}
      />

      <ProtectedRoute
        path={brp('TravelRequestPage')}
        component={TravelRequestPage}
        permissions={[Permissions.ADD_TRAVEL]}
      />

      {/* Edit registered before the detail route (longer path first) */}
      <ProtectedRoute
        path={brp('TravelEditPage', { uuid: TRAVEL_UUID_PARAM })}
        component={TravelEditPage}
        customFilter={({ permissions }) =>
          permissions.includes(Permissions.ADD_TRAVEL) ||
          permissions.includes(Permissions.APPROVE_TRAVEL)
        }
      />

      <ProtectedRoute
        path={brp('TravelDetailsPage', { uuid: TRAVEL_UUID_PARAM })}
        component={TravelDetailsPage}
        customFilter={({ permissions }) =>
          permissions.includes(Permissions.ADD_TRAVEL) ||
          permissions.includes(Permissions.APPROVE_TRAVEL)
        }
      />

      <ProtectedRoute
        path={brp('TravelStatusPage', { status: TRAVEL_STATUS_PARAM })}
        component={TravelStatusPage}
        permissions={[Permissions.ADD_TRAVEL]}
      />

      {/* Legacy permission reuse (`approve_order`) — HLD Open Question; do NOT
          switch to APPROVE_TRAVEL. */}
      <ProtectedRoute
        path={brp('TravelApprovalsPage', { status: TRAVEL_STATUS_PARAM })}
        component={TravelApprovalsPage}
        permissions={[Permissions.APPROVE_ORDER]}
      />

      {/* Empty AND unknown statuses land on the pending tab */}
      <Redirect
        from={brp('TravelApprovalsPage', { status: ':status?' })}
        to={url('TravelApprovalsPage', {
          status: TravelApprovalsPageTabStatuses.PENDING,
        })}
      />
      <Redirect
        from={brp('TravelStatusPage', { status: ':status?' })}
        to={url('TravelStatusPage', {
          status: TravelStatusPageTabStatuses.PENDING,
        })}
      />

      <Route
        path='*'
        render={() => (
          <Redirect
            to={url('TravelStatusPage', {
              status: TravelStatusPageTabStatuses.PENDING,
            })}
          />
        )}
      />
    </Switch>
  )
}
